import React, { useEffect, useState } from 'react';
import styles from './DashboardPage.module.css';
import DateRangePicker, { getToday, getTwelveMonthsAgo } from './time-range-select/DateRangePicker';
import SwDeliverySuccessRate from './sw-delivery-success-rate/SwDeliverySuccessRate';
import MostFrequentFailedStages from './most-frequent-failed-stages/MostFrequentFailedStages';
import AveragePipelineTimeByStage from './average-pipeline-time-by-stage/AveragePipelineTimeByStage';
import FailedSwDeliveryTable from './failed-sw-delivery/FailedSwDeliveryTable';
import DashboardWorkflowSummary from './DashboardWorkflowSummary';
import { useDashboardSummaryByDateRange } from './hooks/use-dashboard-summary-by-date-range';
import { useTheme } from '../../hooks/useTheme';
import { Tile, TileAction, useMaximizeTile } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';

type OverviewSectionId = 'success-rate' | 'failed-stages' | 'pipeline-time' | 'failed-sw-delivery';

const DashboardOverview: React.FC = () => {
  const { theme } = useTheme();
  const [startDate, setStartDate] = useState<string>(() => getTwelveMonthsAgo());
  const [endDate, setEndDate] = useState<string>(() => getToday());
  const [maximizedSection, setMaximizedSection] = useState<OverviewSectionId | null>(null);
  const { setMaximizedTileId } = useMaximizeTile();

  const summaryByDateRange = useDashboardSummaryByDateRange(startDate, endDate);

  const handleMaximize = (sectionId: OverviewSectionId | null) => {
    setMaximizedSection(maximizedSection === sectionId ? null : sectionId);
  };

  useEffect(() => {
    setMaximizedSection(null);
    return () => {
      setMaximizedSection(null);
      setMaximizedTileId(undefined);
    };
  }, [setMaximizedTileId]);

  const sectionClass = theme ? styles.sectionLight : styles.sectionDark;

  return (
    <div className={`${styles.wrapper} ${styles.wrapperOverview}`}>
      <header className={`${styles.header} ${styles.headerWithSummary}`}>
        <div className={`${styles.headerTimeBlock} ${sectionClass}`}>
          <DateRangePicker startDate={startDate} endDate={endDate} onStartDateChange={setStartDate} onEndDateChange={setEndDate} />
        </div>
        <div className={`${styles.headerSummaryBlock} ${sectionClass}`}>
          <DashboardWorkflowSummary pipelineData={summaryByDateRange} />
        </div>
      </header>

      {maximizedSection ? (
        <section className={`${styles.maximizedSection} ${sectionClass}`}>
          {maximizedSection === 'success-rate' && (
            <ReactEDSWrapper
              key="success-rate"
              component={Tile}
              id="success-rate"
              title="SW Delivery Success Rate"
              subtitle="Percentage of successful software deliveries."
              onMaximize={() => handleMaximize('success-rate')}
              onMinimize={() => {
                handleMaximize(null);
                setMaximizedTileId(undefined);
              }}
              actions={[<ReactEDSWrapper component={TileAction} key="maximize" maximizeTileId="success-rate" />]}
            >
              <SwDeliverySuccessRate startDate={startDate} endDate={endDate} />
            </ReactEDSWrapper>
          )}
          {maximizedSection === 'failed-stages' && (
            <ReactEDSWrapper
              key="failed-stages"
              component={Tile}
              id="failed-stages"
              title="Most Frequent Failed Stages"
              subtitle="Stages with the highest average failure rate."
              onMaximize={() => handleMaximize('failed-stages')}
              onMinimize={() => {
                handleMaximize(null);
                setMaximizedTileId(undefined);
              }}
              actions={[<ReactEDSWrapper component={TileAction} key="maximize" maximizeTileId="failed-stages" />]}
            >
              <MostFrequentFailedStages startDate={startDate} endDate={endDate} />
            </ReactEDSWrapper>
          )}
          {maximizedSection === 'pipeline-time' && (
            <ReactEDSWrapper
              key="pipeline-time"
              component={Tile}
              id="pipeline-time"
              title="Average SW Delivery Time by Stage"
              subtitle="Average time spent per stage (in minutes)."
              onMaximize={() => handleMaximize('pipeline-time')}
              onMinimize={() => {
                handleMaximize(null);
                setMaximizedTileId(undefined);
              }}
              actions={[<ReactEDSWrapper component={TileAction} key="maximize" maximizeTileId="pipeline-time" />]}
            >
              <AveragePipelineTimeByStage startDate={startDate} endDate={endDate} />
            </ReactEDSWrapper>
          )}
          {maximizedSection === 'failed-sw-delivery' && (
            <ReactEDSWrapper
              key="failed-sw-delivery"
              component={Tile}
              id="failed-sw-delivery"
              title="Failed SW Delivery"
              subtitle="Stage failure details in the selected date range."
              onMaximize={() => handleMaximize('failed-sw-delivery')}
              onMinimize={() => handleMaximize('failed-sw-delivery')}
              actions={[<ReactEDSWrapper component={TileAction} key="maximize" maximizeTileId="failed-sw-delivery" />]}
            >
              <div className={styles.maximizedTableTileContent}>
                <FailedSwDeliveryTable startDate={startDate} endDate={endDate} isMaximized />
              </div>
            </ReactEDSWrapper>
          )}
        </section>
      ) : (
        <div className={styles.gridContainer}>
          <ReactEDSWrapper
            key="overview-success-rate"
            component={Tile}
            id="success-rate"
            title="SW Delivery Success Rate"
            subtitle="Percentage of successful software deliveries."
            className={`${styles.overviewLeftTop} ${sectionClass}`}
            onMaximize={() => handleMaximize('success-rate')}
            onMinimize={() => handleMaximize('success-rate')}
            actions={[<ReactEDSWrapper component={TileAction} key="maximize" maximizeTileId="success-rate" />]}
          >
            <div className={styles.overviewTileContentFixed}>
              <SwDeliverySuccessRate startDate={startDate} endDate={endDate} />
            </div>
          </ReactEDSWrapper>
          <ReactEDSWrapper
            key="overview-failed-stages"
            component={Tile}
            id="failed-stages"
            title="Most Frequent Failed Stages"
            subtitle="Stages with the highest average failure rate."
            className={`${styles.overviewRightTop} ${sectionClass}`}
            onMaximize={() => handleMaximize('failed-stages')}
            onMinimize={() => handleMaximize('failed-stages')}
            actions={[<ReactEDSWrapper component={TileAction} key="maximize" maximizeTileId="failed-stages" />]}
          >
            <div className={styles.overviewTileContentFixed}>
              <MostFrequentFailedStages startDate={startDate} endDate={endDate} />
            </div>
          </ReactEDSWrapper>
          <ReactEDSWrapper
            key="overview-failed-sw-delivery"
            component={Tile}
            id="failed-sw-delivery"
            title="Failed SW Delivery"
            subtitle="Stage failure details in the selected date range."
            className={`${styles.overviewLeftBottom} ${sectionClass}`}
            onMaximize={() => handleMaximize('failed-sw-delivery')}
            onMinimize={() => handleMaximize('failed-sw-delivery')}
            actions={[<ReactEDSWrapper component={TileAction} key="maximize" maximizeTileId="failed-sw-delivery" />]}
          >
            <FailedSwDeliveryTable startDate={startDate} endDate={endDate} />
          </ReactEDSWrapper>
          <ReactEDSWrapper
            key="overview-pipeline-time"
            component={Tile}
            id="pipeline-time"
            title="Average SW Delivery Time by Stage"
            subtitle="Average time spent per stage (in minutes)."
            className={`${styles.overviewRightBottom} ${sectionClass}`}
            onMaximize={() => handleMaximize('pipeline-time')}
            onMinimize={() => handleMaximize('pipeline-time')}
            actions={[<ReactEDSWrapper component={TileAction} key="maximize" maximizeTileId="pipeline-time" />]}
          >
            <div className={styles.overviewTileContentFixed}>
              <AveragePipelineTimeByStage startDate={startDate} endDate={endDate} />
            </div>
          </ReactEDSWrapper>
        </div>
      )}
    </div>
  );
};

export default DashboardOverview;
